import { Injectable } from '@angular/core';
import {
  ChartConfiguration,
  ChartDataset,
  ElementOptionsByType,
  PluginOptionsByType,
  ScaleChartOptions,
  TooltipLabelStyle,
  TooltipOptions,
} from 'chart.js';
import { min } from 'lodash-es';
import { DeepPartial } from 'node_modules/chart.js/dist/types/utils';

import { Utils } from '@shared/utils/utils';

import { TrialInsightsChartModel } from '../../models/trial-insights-chart.model';

export type PMChartLabel = number[];
export type PMChartType = 'line';

@Injectable({
  providedIn: 'root',
})
export class TrialInsightsPaymentMilestoneChartService
  implements TrialInsightsChartModel<PMChartType, PMChartLabel>
{
  private minYAxes = 0;

  elementOptions = (): DeepPartial<ElementOptionsByType<PMChartType>> => {
    return {
      line: {
        tension: 0,
      },
      point: {
        radius: 0,
      },
    };
  };

  tooltipOptions = (): DeepPartial<TooltipOptions<PMChartType>> => {
    return {
      boxPadding: 6,
      callbacks: {
        label: (tooltipItem) => {
          if (!tooltipItem.raw) {
            return '';
          }

          return Utils.currencyFormatter(<number>tooltipItem.raw);
        },
        labelColor(tooltipItem): TooltipLabelStyle | void {
          return {
            backgroundColor: tooltipItem.dataset.borderColor as string,
            borderColor: tooltipItem.dataset.borderColor as string,
          };
        },
      },
    };
  };

  pluginOptions = (): DeepPartial<PluginOptionsByType<PMChartType>> => {
    return {
      legend: {
        display: false,
      },
      tooltip: this.tooltipOptions(),
    };
  };

  xAxesOptions = (): DeepPartial<ScaleChartOptions<PMChartType>> => {
    return {
      scales: {
        x: {
          beginAtZero: true,
          grid: {
            display: false,
          },
          ticks: {
            display: true,
          },
          title: {
            display: false,
          },
        },
      },
    };
  };

  yAxesOptions = (): DeepPartial<ScaleChartOptions<PMChartType>> => {
    return {
      scales: {
        y: {
          beginAtZero: false,
          border: {
            display: false,
          },
          display: true,
          min: this.minYAxes,
          position: 'right',
          ticks: {
            callback: (value) => {
              return value ? Utils.currencyFormatter(<number>value) : `${value}$`;
            },
            display: true,
          },
        },
      },
    };
  };

  chartOptions = (): ChartConfiguration<PMChartType>['options'] => {
    return {
      elements: this.elementOptions(),
      maintainAspectRatio: false,
      plugins: this.pluginOptions(),
      responsive: true,
      scales: {
        ...this.xAxesOptions().scales,
        ...this.yAxesOptions().scales,
      },
    };
  };

  createChart = (
    datasets?: ChartDataset<PMChartType>[],
    labels?: string[]
  ): ChartConfiguration<PMChartType> => {
    return {
      data: {
        datasets: datasets || [],
        labels: labels || [],
      },
      options: this.chartOptions(),
      type: 'line',
    };
  };

  createDatasets = (paymentMilestones: PMChartLabel): ChartDataset<PMChartType>[] => {
    this.minYAxes = min(paymentMilestones) || 0;

    const chartLineColor = '#438C63';

    return [
      {
        backgroundColor: ['transparent'],
        borderColor: chartLineColor,
        borderWidth: 5,
        data: paymentMilestones,
        hoverBackgroundColor: chartLineColor,
        pointBackgroundColor: chartLineColor,
        pointBorderColor: chartLineColor,
        pointHoverBackgroundColor: chartLineColor,
        pointHoverBorderColor: chartLineColor,
        pointHoverRadius: 2,
        pointRadius: 2,
        pointStyle: 'circle',
      },
    ];
  };
}
